import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Stack,
    Typography,
    Alert,
} from '@mui/material';
import { Send } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

import { useTestSendNotificationTemplate } from '../../hooks/notificationTemplates/useTestSendNotificationTemplate';
import FormInput from '../inputs/FormInput';

// NOTE: The JSON sample-vars editor stays as a raw MUI multiline TextField —
// FormInput's textarea is a styled native <textarea> that doesn't support
// the monospace JSON-editor rendering this dialog needs.

const buildSampleScaffold = (variables = []) => {
    if (!Array.isArray(variables) || variables.length === 0) return '{}';
    const obj = {};
    variables.forEach((name, idx) => {
        obj[name] = `Sample ${idx + 1}`;
    });
    return JSON.stringify(obj, null, 2);
};

const TestSendDialog = ({ open, onClose, templateId, variables }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [to, setTo] = useState('');
    const [sampleText, setSampleText] = useState(() => buildSampleScaffold(variables));
    const [jsonError, setJsonError] = useState(null);

    const mutation = useTestSendNotificationTemplate();

    useEffect(() => {
        if (open) {
            setSampleText(buildSampleScaffold(variables));
            setJsonError(null);
        }
    }, [open, variables]);

    useEffect(() => {
        if (mutation.isSuccess && open) {
            onClose?.();
            mutation.reset();
        }
    }, [mutation.isSuccess, open, onClose, mutation]);

    const handleSend = () => {
        let parsed = {};
        if (sampleText.trim()) {
            try {
                parsed = JSON.parse(sampleText);
                setJsonError(null);
            } catch {
                setJsonError(isArabic ? 'صيغة JSON غير صحيحة' : 'Invalid JSON');
                return;
            }
        }
        mutation.mutate({ id: templateId, data: { to: to.trim(), sample_vars: parsed } });
    };

    const handleClose = () => {
        if (mutation.isPending) return;
        onClose?.();
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
            <DialogTitle>{isArabic ? 'إرسال إيميل تجريبي' : 'Send a test email'}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 0.5 }}>
                    <Alert severity="info">
                        {isArabic
                            ? 'يدعم الإرسال التجريبي قناة البريد الإلكتروني فقط حالياً.'
                            : 'Test send currently supports the email channel only.'}
                    </Alert>

                    <FormInput
                        type="text"
                        name="to"
                        label={isArabic ? 'البريد المستلم' : 'Recipient email'}
                        value={to}
                        onChange={(e) => setTo(e.target.value)}
                        disabled={mutation.isPending}
                        required
                        placeholder="user@example.com"
                    />

                    <Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                            {isArabic ? 'متغيرات تجريبية (JSON)' : 'Sample variables (JSON)'}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={5}
                            value={sampleText}
                            onChange={(e) => setSampleText(e.target.value)}
                            error={!!jsonError}
                            helperText={jsonError || ''}
                            sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 13 } }}
                            disabled={mutation.isPending}
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="inherit" disabled={mutation.isPending}>
                    {isArabic ? 'إلغاء' : 'Cancel'}
                </Button>
                <Button
                    variant="contained"
                    startIcon={<Send />}
                    onClick={handleSend}
                    disabled={!to.trim() || mutation.isPending}
                >
                    {mutation.isPending
                        ? (isArabic ? 'جاري الإرسال...' : 'Sending...')
                        : (isArabic ? 'إرسال' : 'Send')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TestSendDialog;
